const TIMEZONE_BR = 'America/Sao_Paulo'

/**
 * Sentinela de "vigência em aberto". O backend representa o fim de vigência
 * aberto como 9999-12-31 23:59:59 UTC (ver AtualizacaoCustoService.VigenciaAberta).
 * Como 31/12/9999 23:59 BRT não é representável em UTC com ano de 4 dígitos
 * (viraria +010000-01-01T02:59Z), esse instante NÃO deve ser convertido para o
 * fuso Brasil na exibição (o que mostraria incorretamente 20:59).
 */
export const VALOR_MAXIMO_UTC = '9999-12-31T23:59:59.000Z'

/**
 * Verifica se um timestamp UTC ISO corresponde ao sentinela de vigência em aberto
 * (ano/mês/dia = 9999-12-31 e hora = 23:59:59 em UTC). Aceita tanto a forma com
 * milissegundos ("...59.000Z") quanto sem ("9999-12-31T23:59:59Z", vinda do backend).
 */
export function ehValorMaximoUTC (utcISO) {
  if (!utcISO) return false
  try {
    const d = new Date(utcISO)
    if (Number.isNaN(d.getTime())) return false
    return d.getUTCFullYear() === 9999 &&
      d.getUTCMonth() === 11 &&
      d.getUTCDate() === 31 &&
      d.getUTCHours() === 23 &&
      d.getUTCMinutes() === 59 &&
      d.getUTCSeconds() === 59
  } catch {
    return false
  }
}

/**
 * Retorna a data de HOJE no fuso Brasil, no formato YYYY-MM-DD.
 * USO EXCLUSIVO: value default de inputs type="date" em formulários.
 *
 * Exemplo: 01/07/2026 23:00 BRT → "01/07/2026" (não avança para 02/07)
 */
/**
 * Retorna o instante atual como timestamp UTC ISO (ex: '2026-10-05T17:30:00.000Z').
 * USO: valor default de campos de timestamp (DateTime).
 */
export function agoraUtcISO () {
  return new Date().toISOString()
}

export function dataHojeBR () {
  const hoje = new Intl.DateTimeFormat('pt-BR', {
    timeZone: TIMEZONE_BR,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(new Date())

  // "01/07/2026" → ["01", "07", "2026"] → "2026-07-01"
  return hoje.split('/').reverse().join('-')
}

/**
 * Formata uma data ISO (DateOnly) para exibição pt-BR.
 * SEMPRE usar timeZone: 'UTC' para preservar a data exata recebida do backend.
 *
 * Input : "2026-07-03"  ou  "2026-07-03T00:00:00"
 * Output: "03/07/2026"
 */
export function formatarDataBR (dataISO) {
  if (!dataISO) return '-'
  try {
    return new Intl.DateTimeFormat('pt-BR', {
      timeZone: 'UTC',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(new Date(dataISO))
  } catch {
    return String(dataISO)
  }
}

/**
 * Formata um timestamp ISO (DateTime) para exibição pt-BR com hora.
 * Converte de UTC para America/Sao_Paulo.
 *
 * Input : "2026-07-03T04:20:06.128Z"
 * Output: "03/07/2026 01:20:06"
 */
export function formatarDataHoraBR (dataISO) {
  if (!dataISO) return '-'
  if (ehValorMaximoUTC(dataISO)) return '31/12/9999 23:59:59'
  try {
    return new Intl.DateTimeFormat('pt-BR', {
      timeZone: TIMEZONE_BR,
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    }).format(new Date(dataISO))
  } catch {
    return String(dataISO)
  }
}

/**
 * Verifica se uma data (DateOnly) está vencida em relação à data de hoje no Brasil.
 *
 * Input : "2026-07-01"
 * Output: true (se hoje for 03/07/2026 BRT)
 */
export function isVencido (dataISO) {
  if (!dataISO) return false
  try {
    const hoje = dataHojeBR() // "2026-07-03"
    const dataStr = dataISO.includes('T') ? dataISO.split('T')[0] : dataISO
    return dataStr < hoje
  } catch {
    return false
  }
}

/**
 * Extrai os componentes de data/hora no fuso Brasil a partir de um timestamp UTC ISO.
 * Retorna { ano, mes, dia, hora, minuto, segundo } ou null.
 */
export function componentesBrasil (utcISO) {
  if (!utcISO) return null
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: TIMEZONE_BR,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    }).formatToParts(new Date(utcISO))
    const get = (t) => Number(parts.find(p => p.type === t)?.value)
    return {
      ano: get('year'),
      mes: get('month'),
      dia: get('day'),
      hora: get('hour'),
      minuto: get('minute'),
      segundo: get('second')
    }
  } catch {
    return null
  }
}

/**
 * Converte componentes de data/hora no fuso Brasil para um timestamp UTC ISO.
 * Recebe { ano, mes, dia, hora, minuto, segundo }.
 */
export function brasilParaUtcISO ({ ano, mes, dia, hora, minuto, segundo }) {
  const comoUtc = Date.UTC(ano, mes - 1, dia, hora, minuto, segundo || 0)
  // Descobre o offset real de Brasília nesse instante (robusto a eventuais mudanças)
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: TIMEZONE_BR,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  }).formatToParts(new Date(comoUtc))
  const get = (t) => Number(parts.find(p => p.type === t)?.value)
  const brComoUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'))
  const offsetMs = comoUtc - brComoUtc
  return new Date(comoUtc + offsetMs).toISOString()
}

/**
 * Formata um timestamp UTC ISO para exibição pt-BR, respeitando o nível de precisão.
 * nivel: 'hora' | 'minuto' | 'segundo'
 */
export function formatarTimestampBR (utcISO, nivel = 'minuto') {
  if (!utcISO) return ''
  // Sentinela de vigência em aberto: exibir como 23:59 sem converter o fuso
  // (a conversão UTC→BRT mostraria incorretamente 20:59).
  if (ehValorMaximoUTC(utcISO)) {
    const data = '31/12/9999'
    if (nivel === 'hora') return `${data} 23h`
    if (nivel === 'segundo') return `${data} 23:59:59`
    return `${data} 23:59`
  }
  const c = componentesBrasil(utcISO)
  if (!c) return ''
  const pad = (n) => String(n).padStart(2, '0')
  const data = `${pad(c.dia)}/${pad(c.mes)}/${c.ano}`
  if (nivel === 'hora') return `${data} ${pad(c.hora)}h`
  if (nivel === 'segundo') return `${data} ${pad(c.hora)}:${pad(c.minuto)}:${pad(c.segundo)}`
  return `${data} ${pad(c.hora)}:${pad(c.minuto)}`
}
